import { unknownPlaceholders } from "@components/scheduling/settings/settingsValidation";

const FIELD = /\{([^{}]*)\}/g;

// What the clinic sees in the preview. Real values come from the booking.
const SAMPLE = {
  patient_first_name: "Mary",
  patient_name: "Mary Smith",
  clinic_name: "Your clinic",
  treatment_name: "Cleaning",
  appointment_date: "Thursday, 10 September",
  appointment_time: "2:30 PM",
  clinic_phone: "(555) 123-4567",
};

const textareaClass =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

/**
 * The text a patient gets when a campaign call books them in.
 *
 * The placeholder list and the segment length come from the server, so this
 * never drifts from what the backend will accept. It validates them here too,
 * so the clinic does not have to press Save to find out.
 */
export default function SmsTemplateEditor({
  enabled, template, placeholders, segmentChars, onToggle, onTemplateChange,
}) {
  const known = placeholders || [];
  const unknown = unknownPlaceholders(template, known);
  const preview = String(template || "").replace(FIELD, (whole, name) =>
    (SAMPLE[name] !== undefined ? SAMPLE[name] : whole));
  const tooLong = String(template || "").length > (segmentChars || 160);
  const empty = !String(template ?? "").trim();

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-gray-700">
        {/* No aria-label: the wrapping label is the accessible name, so what
            a screen reader announces is what the clinic can read. */}
        <input
          type="checkbox"
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          checked={Boolean(enabled)}
          onChange={(e) => onToggle(e.target.checked)}
        />
        Text a booking confirmation when a campaign call books an appointment
      </label>

      {enabled && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1"
                   htmlFor="sms-template">
              Message
            </label>
            <textarea id="sms-template" rows={4} className={textareaClass} value={template}
                      onChange={(e) => onTemplateChange(e.target.value)} />
          </div>

          {empty && (
            <p className="text-xs text-red-600">
              Write the message a patient gets, or turn the confirmation off. The server
              will not store an empty one.
            </p>
          )}

          <div className="flex flex-wrap gap-1">
            {known.map((name) => (
              <button
                key={name}
                type="button"
                className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-200"
                onClick={() => onTemplateChange(`${template || ""}{${name}}`)}
              >
                {`{${name}}`}
              </button>
            ))}
          </div>

          {unknown.length > 0 && (
            <p className="text-xs text-red-600">
              {unknown.map((name) => `{${name}}`).join(", ")}{" "}
              {unknown.length === 1 ? "is not a placeholder" : "are not placeholders"} the
              clinic can use. Pick one from the list above.
            </p>
          )}

          {tooLong && (
            <p className="text-xs text-amber-700">
              Over {segmentChars} characters, so this costs more than one text message
              to send.
            </p>
          )}

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="mb-1 text-xs font-medium text-gray-500">Preview</p>
            <p className="text-sm text-gray-800">{preview}</p>
          </div>
        </>
      )}
    </div>
  );
}
